import React, { useState } from 'react';
import {
  User, CalendarDays, School, StickyNote, Loader2, Save, Plus, X,
  Activity, AlertTriangle, AlertOctagon, Radar, Sparkles, ChevronRight, RefreshCw
} from 'lucide-react';
import { updateActionMeta, addCustomAction, runDriftCheck, formatApiError } from '../services/api';

export const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', cls: 'bg-slate-100 text-slate-600' },
  { value: 'in_progress', label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
  { value: 'blocked', label: 'Blocked', cls: 'bg-red-100 text-red-700' },
  { value: 'done', label: 'Done', cls: 'bg-emerald-100 text-emerald-700' },
];

export const CATEGORY_LABELS = {
  leadership: 'Leadership & General Management', riskFinancing: 'Risk Financing', hrPractices: 'HR Practices',
  incidentPrevention: 'Incident Prevention', enterpriseRisk: 'Enterprise Risk Assessment', riskCommunication: 'Risk Communication',
  metrics: 'Metrics and Measures', districtPrograms: 'District Wide Programs', employeeSafety: 'Employee Safety Programs',
  equity: 'Equity & Violence Prevention', totalHealth: 'Total Health', discreteServices: 'Discrete Services',
  supplyChain: 'Supply Chain & Construction', injuryManagement: 'Injury Management', claimsManagement: 'Claims Management',
};

export const statusOf = (meta, completed, id) => meta?.[id]?.status || (completed.includes(id) ? 'done' : 'not_started');
export const isOverdue = (m) => m?.due_date && m.status !== 'done' && new Date(m.due_date) < new Date(new Date().toDateString());

const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

export function ActionMetaEditor({ action, meta, campuses, onSaved }) {
  const [form, setForm] = useState({
    owner: meta?.owner || '', due_date: meta?.due_date || '', status: meta?.status || 'not_started',
    campus_id: meta?.campus_id || '', notes: meta?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await updateActionMeta(action.id, form);
      onSaved(action.id, res.data.meta, res.data.completed_actions);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`meta-editor-${action.id}`}>
      <label className="text-xs text-slate-500 flex flex-col gap-1">
        <span className="flex items-center gap-1"><User className="w-3 h-3" /> Owner</span>
        <input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="e.g. Dana Ruiz, Facilities" className={inputCls} data-testid={`owner-input-${action.id}`} />
      </label>
      <label className="text-xs text-slate-500 flex flex-col gap-1">
        <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Due date</span>
        <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls} data-testid={`due-input-${action.id}`} />
      </label>
      <label className="text-xs text-slate-500 flex flex-col gap-1">
        <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> Status</span>
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid={`status-select-${action.id}`}>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </label>
      <label className="text-xs text-slate-500 flex flex-col gap-1">
        <span className="flex items-center gap-1"><School className="w-3 h-3" /> Campus</span>
        <select value={form.campus_id} onChange={(e) => setForm({ ...form, campus_id: e.target.value })} className={inputCls} data-testid={`campus-select-${action.id}`}>
          <option value="">District-wide</option>
          {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>
      <label className="text-xs text-slate-500 flex flex-col gap-1 sm:col-span-2 lg:col-span-3">
        <span className="flex items-center gap-1"><StickyNote className="w-3 h-3" /> Notes / evidence</span>
        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Board approved 5/12; policy draft in review" className={inputCls} data-testid={`notes-input-${action.id}`} />
      </label>
      <div className="flex items-end">
        <button onClick={save} disabled={saving} className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2" data-testid={`save-meta-${action.id}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
      {error && <p className="text-xs text-red-600 sm:col-span-2 lg:col-span-4">{error}</p>}
    </div>
  );
}

export function AddActionModal({ phases, campuses, defaultPhase = 0, onClose, onAdded }) {
  const [form, setForm] = useState({ phase_index: defaultPhase, item: '', recommendation: '', category: 'leadership', priority: 'medium', owner: '', due_date: '', campus_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, phase_index: Number(form.phase_index) };
      ['owner', 'due_date', 'campus_id'].forEach((k) => { if (!payload[k]) delete payload[k]; });
      const res = await addCustomAction(payload);
      onAdded(res.data);
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-lg animate-in" data-testid="add-action-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-outfit text-lg font-bold text-slate-900">Add Your Own Action</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}
          <input value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} required placeholder="Action title (e.g. Complete playground equipment audit)" className={inputCls} data-testid="custom-item-input" />
          <textarea value={form.recommendation} onChange={(e) => setForm({ ...form, recommendation: e.target.value })} required rows={3} placeholder="What exactly will be done?" className={inputCls} data-testid="custom-recommendation-input" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.phase_index} onChange={(e) => setForm({ ...form, phase_index: e.target.value })} className={inputCls} data-testid="custom-phase-select">
              {phases.map((p, i) => <option key={i} value={i}>{p.name}</option>)}
            </select>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="custom-category-select">
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls}>
              <option value="high">High priority</option><option value="medium">Medium priority</option><option value="low">Low priority</option>
            </select>
            <select value={form.campus_id} onChange={(e) => setForm({ ...form, campus_id: e.target.value })} className={inputCls}>
              <option value="">District-wide</option>
              {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="Owner" className={inputCls} />
            <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls} />
          </div>
          <button type="submit" disabled={saving} className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="custom-action-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add to Roadmap
          </button>
        </div>
      </form>
    </div>
  );
}

const SEV = { high: 'border-red-200 bg-red-50 text-red-800', medium: 'border-amber-200 bg-amber-50 text-amber-800', low: 'border-slate-200 bg-slate-50 text-slate-700' };

export function DriftPanel({ report, onUpdated }) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const run = async () => {
    setRunning(true);
    setError('');
    try {
      const res = await runDriftCheck();
      onUpdated(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  const m = report?.metrics;

  return (
    <div className="bg-slate-900 text-white rounded-2xl p-6" data-testid="drift-panel">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Radar className="w-5 h-5 text-emerald-400" /></div>
          <div>
            <h3 className="font-outfit text-lg font-semibold">AI Drift Detection</h3>
            <p className="text-sm text-slate-400">{report ? `Last check ${new Date(report.generated_at).toLocaleString()}` : 'Find stalled, overdue, and unowned work before the board does.'}</p>
          </div>
        </div>
        <button onClick={run} disabled={running} className="px-4 py-2.5 bg-emerald-500 text-slate-900 rounded-lg font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 flex items-center gap-2" data-testid="run-drift-btn">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : report ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
          {running ? 'Analyzing...' : report ? 'Re-run Check' : 'Run Drift Check'}
        </button>
      </div>
      {error && <p className="text-sm text-red-300 mb-3">{error}</p>}

      {m && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            ['Complete', `${m.completion_pct}%`, 'text-emerald-400'],
            ['Overdue', m.overdue.length, m.overdue.length ? 'text-red-400' : 'text-slate-200'],
            ['Unowned', m.unowned.length, m.unowned.length ? 'text-amber-400' : 'text-slate-200'],
            ['Stalled 30d+', m.stalled.length, m.stalled.length ? 'text-amber-400' : 'text-slate-200'],
          ].map(([label, val, cls]) => (
            <div key={label} className="bg-white/5 rounded-xl p-3">
              <p className={`text-2xl font-outfit font-bold ${cls}`}>{val}</p>
              <p className="text-xs text-slate-400">{label}</p>
            </div>
          ))}
        </div>
      )}

      {report && (
        <div className="space-y-3">
          <p className="text-slate-200 leading-relaxed" data-testid="drift-summary">{report.summary}</p>
          {report.alerts?.length > 0 && (
            <div className="grid gap-2">
              {report.alerts.map((a, i) => (
                <div key={i} className={`border rounded-lg px-4 py-3 text-sm ${SEV[a.severity] || SEV.low}`} data-testid={`drift-alert-${i}`}>
                  <p className="font-semibold flex items-center gap-2">
                    {a.severity === 'high' ? <AlertOctagon className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}{a.title}
                  </p>
                  <p className="mt-0.5 opacity-90">{a.detail}</p>
                </div>
              ))}
            </div>
          )}
          {report.recommended_focus?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Focus for the next two weeks</p>
              {report.recommended_focus.map((f, i) => (
                <p key={i} className="text-sm text-slate-200 flex items-start gap-2"><ChevronRight className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />{f}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
